import React, { useEffect } from "react";
import { Mic, Loader2 } from "lucide-react";
import { useAuthStore } from "./stores/authStore";
import { usePartyStore } from "./stores/partyStore";
import { useThemeStore } from "./stores/themeStore";
import { useIsMobile } from "./hooks/useIsMobile";
import { useAuthListener } from "./hooks/useAuthListener";
import { usePartySync } from "./hooks/usePartySync";
import { useQueueSync } from "./hooks/useQueueSync";
import { useMembersSync } from "./hooks/useMembersSync";

import { Header } from "./components/common/Header";
import { LoginView } from "./components/auth/LoginView";
import { LobbyView } from "./components/lobby/LobbyView";
import { PartyControllerView } from "./views/PartyControllerView";
import { TVDisplayView } from "./components/player/TVDisplayView";
import { PartyWrappedModal } from "./components/summary/PartyWrappedModal";

export const App: React.FC = () => {
  const { user, loading } = useAuthStore();
  const { isTVMode } = usePartyStore();
  const { theme } = useThemeStore();
  const isMobile = useIsMobile();

  // Expose store instances in dev for testing
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as any).__partyStore = usePartyStore;
      (window as any).__authStore = useAuthStore;
      (window as any).__themeStore = useThemeStore;
    }
  }, []);

  // Apply theme class to html and body
  useEffect(() => {
    const themeClass = theme === "dark" ? "" : `theme-${theme}`;
    document.documentElement.className = themeClass;
    document.body.className = themeClass;
  }, [theme]);

  // Hook sync listeners
  useAuthListener();
  usePartySync(user?.currentPartyId || null);
  useQueueSync(user?.currentPartyId || null);
  useMembersSync(user?.currentPartyId || null);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center space-y-4">
        <div className="w-16 h-16 rounded-3xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-xl animate-pulse">
          <Mic className="w-8 h-8 text-white" />
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span>Starting Squad-a-Roke...</span>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!user) {
    return (
      <div className="min-h-screen bg-background text-text-main flex flex-col">
        <Header />
        <main className="flex-1">
          <LoginView />
        </main>
      </div>
    );
  }

  // Signed in, not currently in a party -> Lobby
  if (!user.currentPartyId) {
    return (
      <div className="min-h-screen bg-background text-text-main flex flex-col">
        <Header />
        <main className="flex-1 pb-10">
          <LobbyView />
        </main>
        <PartyWrappedModal />
      </div>
    );
  }

  // Inside a party: TV Mode vs Mobile Controller (TV Mode disabled on mobile screens)
  if (isTVMode && !isMobile) {
    return (
      <>
        <TVDisplayView />
        <PartyWrappedModal />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background text-text-main flex flex-col">
      <Header />
      <main className="flex-1 pb-12">
        <PartyControllerView />
      </main>
      <PartyWrappedModal />
    </div>
  );
};
